import { useGlobalState } from '@vindr/workspace-core/contexts/GlobalContext';
// import { useGetAccounts } from '@vindr/services/actions/account.action';
import { useGetDepartments } from '@vindr/services/actions/department.action';
import { useDeliverOrders } from '@vindr/services/actions/order.action';
import { NewOrder } from '@vindr/services/apis/order.api';
import { MESSAGE } from '@vindr/services/constants/message.constant';
import { PERMISSIONS } from '@vindr/services/constants/permissions.constant';
import { FormItem } from '@vindr/ui';
import dayjs from '@vindr/utils/day';
// import { getDateFormatted } from '@vindr/utils/helpers/common.helper';
import { Button, Checkbox, Col, Form, Modal, Row, Select, Space } from 'antd';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useToggle from '@vindr/utils/hooks/useToggle';

const DeliverModal = ({
  order,
  open,
  onClose,
  onFinish,
}: {
  order: NewOrder;
  open: boolean;
  onClose: () => void;
  onFinish: () => void;
}) => {
  const { t } = useTranslation('Vindoc');
  const { message, keycloak } = useGlobalState();
  const { mutate: deliverOrders } = useDeliverOrders();
  const [deliverForm] = Form.useForm();
  // const accounts = useGetAccounts({ offset: 0, limit: 100 });
  // const accountData = useMemo(() => accounts?.data?.data, [accounts]);
  const { value: defaultDeliverLocation, setValue: setDefaultDeliverLocation } = useToggle(true);
  const departments = useGetDepartments({
    offset: 0,
    limit: 100,
    institution_ids: [order?.institution_id],
    active: true,
  });

  const departmentsData = useMemo(
    () => (departments?.data?.data || []).filter((it) => !!it?.result_delivery_location),
    [departments]
  );

  const user = keycloak.getUserInfo();

  useEffect(() => {
    if (defaultDeliverLocation)
      deliverForm.setFieldValue('result_delivery_location', order?.department?.code);
  }, [defaultDeliverLocation, order]);

  const handleSave = async () => {
    if (!keycloak.hasOneOfPerm([PERMISSIONS.order.deliverreport])) {
      message.error(t(MESSAGE.ERROR.EMSG_0002));
      return;
    }

    try {
      const values = await deliverForm.validateFields();
      // const deliveredBy = accountData.find((it) => it.id === values.delivered_by);
      const department = (departmentsData || []).find(
        (it) => it.code === values.result_delivery_location
      );

      deliverOrders(
        {
          order_ids: [order?.id],
          delivered_at: dayjs(values.delivered_at).toISOString(),
          delivered_by_name: user.fullname,
          delivered_by_username: user.username,
          institution_id: order?.institution_id,
          result_delivery_location: department?.result_delivery_location,
        },
        {
          onSuccess: () => {
            message.success(t(MESSAGE.SUCCESS.SCD_8001));
            deliverForm.resetFields();
            onFinish();
          },
          onError: (error: any) => {
            const error_message =
              (error?.response?.data?.error_code &&
                MESSAGE.ERROR[error?.response?.data?.error_code]) ||
              'Update error';

            message.error(t(error_message));
          },
        }
      );
    } catch (error: any) {
      console.log(error);
      message.error(t(MESSAGE.ERROR.EMSG_0001));
    }
  };

  return (
    <Modal
      title={t('Deliver Report')}
      open={open}
      onCancel={onClose}
      footer={null}
      width={450}
      forceRender
      destroyOnClose
    >
      <Form
        form={deliverForm}
        layout="vertical"
        initialValues={{
          delivered_at: dayjs().toDate(),
          delivered_by: user?.id,
          result_delivery_location: order?.department?.code,
        }}
        onFinishFailed={() => message.error(t(MESSAGE.ERROR.EMSG_0001))}
      >
        <FormItem.DatePicker
          label={t('Delivered At')}
          name="delivered_at"
          placeholder="Select"
          showTime
          disabled
          rules={[{ required: true, message: t('Please select date') }]}
        />

        <FormItem.Technician
          name="delivered_by"
          label={t('Delivered By')}
          placeholder={t('Select')}
          disabled
          selected={{ value: user?.id, label: user?.fullname || user?.username }}
        />

        <Form.Item
          name="result_delivery_location"
          label={
            <div className="flex-start flex flex-col">
              <span>{t('Result Delivery Location')}</span>
              <Checkbox
                checked={defaultDeliverLocation}
                onChange={(e) => setDefaultDeliverLocation(e.target.checked)}
              >
                {t('Default')}
              </Checkbox>
            </div>
          }
          className="mb-2"
        >
          <Select placeholder={t('Select')} disabled={defaultDeliverLocation}>
            {departmentsData?.length
              ? departmentsData.map((it, idx) => (
                  <Select.Option key={idx} value={it?.code}>
                    {it?.result_delivery_location}
                  </Select.Option>
                ))
              : null}
          </Select>
        </Form.Item>

        <Row justify="end" className="mt-6" gutter={[16, 16]}>
          <Col>
            <Space>
              <Button
                onClick={() => {
                  deliverForm.resetFields();
                  onClose();
                }}
              >
                {t('Cancel')}
              </Button>
              <Button type="primary" htmlType="submit" onClick={() => handleSave()}>
                {t('Save')}
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default DeliverModal;
